import { useTheme } from "@emotion/react";
import { faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { BillingRuleEntity } from "@ternary/api-lib/core/types/BillingRule";
import { actions } from "@ternary/api-lib/telemetry";
import { AutosaveIndicator } from "@ternary/api-lib/ui-lib/components/AutosaveIndicator";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import { Tooltip } from "@ternary/api-lib/ui-lib/components/Tooltip";
import { keyBy, orderBy } from "lodash";
import React, { useState } from "react";
import { Navigate, useLocation } from "react-router";
import useGetUsersByTenantID from "../../../api/core/hooks/useGetUsersByTenantID";
import paths from "../../../constants/paths";
import { useActivityTracker } from "../../../context/ActivityTrackerProvider";
import useAuthenticatedUser from "../../../hooks/useAuthenticatedUser";
import useGatekeeper from "../../../hooks/useGatekeeper";
import { useNavigateWithSearchParams } from "../../../lib/react-router";
import { useMspStore } from "../../../lib/zustand";
import ConfirmationModal from "../../../ui-lib/components/ConfirmationModal";
import SelectCheckbox from "../../../ui-lib/components/SelectCheckbox";
import TextInput from "../../../ui-lib/components/TextInput";
import { AlertType, postAlert } from "../../../utils/alerts";
import getMergeState from "../../../utils/getMergeState";
import { useGetTenantsByParentTenantID } from "../../global-admin/hooks/useGetTenantsByParentTenantID";
import copyText from "../copyText";
import queryKeys from "../hooks/queryKeys";
import { useDeleteBillingRule } from "../hooks/useDeleteBillingRule";
import { useGetBillingRulesByTenantID } from "../hooks/useGetBillingRulesByTenantID";
import { useUpdateBillingRule } from "../hooks/useUpdateBillingRule";
import { BillingRuleTable } from "./BillingRuleTable";

type Interaction = BillingRuleTable.Interaction;

type State = {
  dimensionNames: string[];
  searchText: string;
  selectedRuleID: string | null;
  selectedTenantIDs: string[];
  showModal: boolean;
};

const initialState: State = {
  dimensionNames: [],
  searchText: "",
  selectedRuleID: null,
  selectedTenantIDs: [],
  showModal: false,
};

const defaultRules = [];

export function BillingRuleManagementContainer() {
  const activityTracker = useActivityTracker();
  const authenticatedUser = useAuthenticatedUser();
  const gatekeeper = useGatekeeper();
  const location = useLocation();
  const navigate = useNavigateWithSearchParams();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const isMspAdminLocation = location.pathname.startsWith(paths._mspMgmt);

  //
  // State
  //

  const mspStore = useMspStore();

  const parentTenantID = mspStore.selectedParentTenantID as string;

  const tenantID = isMspAdminLocation
    ? parentTenantID
    : authenticatedUser.tenant.id;

  const [state, setState] = useState<State>(initialState);

  const mergeState = getMergeState(setState);

  //
  // Queries
  //

  const { data: _rules = defaultRules, isLoading: isLoadingRules } =
    useGetBillingRulesByTenantID(tenantID, {
      enabled: !!tenantID,
    });

  const { data: users = [], isLoading: isLoadingUsers } = useGetUsersByTenantID(
    tenantID,
    { enabled: !!tenantID }
  );

  const { data: _mspTenants = [], isLoading: isLoadingMspTenants } =
    useGetTenantsByParentTenantID(parentTenantID, {
      enabled: gatekeeper.canReadTenantsPartner && !!parentTenantID,
      includeParentTenant: isMspAdminLocation,
    });

  //
  // Mutations
  //

  const { isPending: isDeletingRule, mutate: deleteRule } =
    useDeleteBillingRule({
      onError: () => {
        postAlert({
          message: copyText.errorDeletingBillingRuleMessage,
          type: AlertType.ERROR,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.billingRules(tenantID),
        });

        mergeState({ selectedRuleID: null, showModal: false });

        postAlert({
          message: copyText.successDeletingBillingRuleMessage,
          type: AlertType.SUCCESS,
        });
      },
    });

  const { isPending: isUpdatingRule, mutate: updateRule } =
    useUpdateBillingRule({
      onMutate: async (params) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: queryKeys.billingRules(tenantID),
        });

        // Get current rules and store for potential rollback
        const previousRules =
          queryClient.getQueryData<BillingRuleEntity[]>(
            queryKeys.billingRules(tenantID)
          ) ?? [];

        if (params.priority !== undefined) {
          const existingRule = previousRules.find(
            (rule) => rule.id === params.ruleID
          );

          if (!existingRule?.priority) return { previousRules };

          const fromPriority = existingRule.priority;
          const toPriority = params.priority;

          queryClient.setQueryData(
            queryKeys.billingRules(tenantID),
            (oldRules: BillingRuleEntity[]): BillingRuleEntity[] => {
              const updatedRules = oldRules.map((rule) => {
                if (rule.id === params.ruleID) {
                  return { ...rule, priority: toPriority };
                }

                // Shift priorities for rules in between the from/to positions
                if (fromPriority < toPriority) {
                  // Moving down
                  if (
                    rule.priority > fromPriority &&
                    rule.priority <= toPriority
                  ) {
                    return { ...rule, priority: rule.priority - 1 };
                  }
                } else {
                  // Moving up
                  if (
                    rule.priority >= toPriority &&
                    rule.priority < fromPriority
                  ) {
                    return { ...rule, priority: rule.priority + 1 };
                  }
                }

                return rule;
              });

              const sortedRules = updatedRules.sort(
                (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
              );

              return sortedRules;
            }
          );
        }

        return { previousRules };
      },

      onError: () => {
        postAlert({
          message: copyText.errorUpdatingBillingRuleMessage,
          type: AlertType.ERROR,
        });
      },
      onSuccess: (_, params) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.billingRules(tenantID),
        });

        // Only show the success message for form updates.
        // Don't show it for changing priority via the table drag and drop.
        if (params.priority !== undefined) return;

        postAlert({
          message: copyText.successUpdatingBillingRuleMessage,
          type: AlertType.SUCCESS,
        });
      },
    });

  //
  // Computed Values
  //

  const usersKeyedByID = keyBy(users, "id");

  let rules = _rules.map((rule) => ({
    ...rule,
    createdByEmail: usersKeyedByID[rule.createdByID]?.email ?? "",
  }));

  if (state.selectedTenantIDs.length > 0) {
    rules = rules.filter((ruleSet) => {
      if (state.selectedTenantIDs.includes("ALL")) {
        return ruleSet.applyToAllTenants;
      }

      return ruleSet.applyToTenantIDs.some((tenantID) =>
        state.selectedTenantIDs.includes(tenantID)
      );
    });
  }

  if (state.searchText.length > 0) {
    rules = rules.filter((rule) => {
      return [rule.id, rule.name, rule.createdByEmail]
        .join(" ")
        .toLowerCase()
        .includes(state.searchText.toLowerCase());
    });
  }

  //
  // Interaction Handlers
  //

  function handleDeleteRuleSet() {
    if (!state.selectedRuleID) return;

    activityTracker.captureAction(actions.CLICK_BILLING_RULE_DELETE);

    deleteRule({ ruleID: state.selectedRuleID });
  }

  function handleInteraction(interaction: Interaction) {
    switch (interaction.type) {
      case BillingRuleTable.INTERACTION_DELETE_CLICKED: {
        mergeState({
          selectedRuleID: interaction.ruleSetID,
          showModal: true,
        });
        return;
      }
      case BillingRuleTable.INTERACTION_EDIT_CLICKED: {
        activityTracker.captureAction(
          actions.CLICK_BILLING_RULE_UPDATE_FORM_OPEN
        );

        const path = isMspAdminLocation
          ? paths._mspBillingRulesUpdate
          : paths._costAssistReallocationsUpdate;

        navigate(path.replace(":ruleID", interaction.ruleID));
        return;
      }
      case BillingRuleTable.INTERACTION_ROW_ORDER_CHANGED: {
        activityTracker.captureAction(
          actions.CLICK_BILLING_RULE_CHANGE_PRIORITY
        );

        const oldIndex = interaction.oldIndex;
        const newIndex = interaction.newIndex;

        const fromObject = rules[oldIndex];
        const toObject = rules[newIndex];

        if (!fromObject || !toObject) return;

        updateRule({
          ruleID: fromObject.id,
          priority: toObject.priority,
        });

        return;
      }
    }
  }

  function handleClickCreateRule() {
    activityTracker.captureAction(actions.CLICK_BILLING_RULE_CREATE_FORM_OPEN);

    const path = isMspAdminLocation
      ? paths._mspBillingRulesCreate
      : paths._costAssistReallocationsCreate;

    navigate(path);
  }

  //
  // Render
  //

  if (isMspAdminLocation && !gatekeeper.canAccessMspAdmin) {
    return <Navigate to={paths._home} replace />;
  }

  const tenantOptions = [
    { label: copyText.appliesToAllLabel, value: "ALL" },
    ...orderBy(_mspTenants, "name").map((tenant) => ({
      label: tenant.name,
      value: tenant.id,
    })),
  ];

  return (
    <>
      {state.showModal && (
        <ConfirmationModal
          isLoading={isDeletingRule}
          message={copyText.deleteRuleModalMessage}
          title={copyText.deleteRuleModalTitle}
          variant="danger"
          onCancel={() => mergeState({ showModal: false })}
          onConfirm={handleDeleteRuleSet}
        />
      )}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        marginBottom={theme.space_sm}
      >
        <Text appearance="h3">{copyText.billingRulesPageTitle}</Text>
        <Flex alignItems="center" justifyContent="right">
          <AutosaveIndicator isSaving={isUpdatingRule} />
          {isMspAdminLocation && (
            <Box width={280} marginRight={theme.space_md}>
              <SelectCheckbox
                isLoading={isLoadingMspTenants}
                options={tenantOptions}
                placeholder={copyText.selectTenantsPlacholder}
                selectedValues={state.selectedTenantIDs}
                onChange={(tenantIDs) => {
                  activityTracker.captureAction(
                    actions.SELECT_BILLING_RULE_FILTER_BY_TENANT,
                    { tenantIDs }
                  );

                  mergeState({ selectedTenantIDs: tenantIDs });
                }}
              />
            </Box>
          )}
          <Box width={250} marginRight={theme.space_md}>
            <TextInput
              iconStart={
                <Icon color={theme.text_color_secondary} icon={faSearch} />
              }
              placeholder={copyText.searchInputPlaceholder}
              onChange={(event) =>
                mergeState({ searchText: event.target.value })
              }
            />
          </Box>
          <Tooltip
            content={copyText.createRuleTooltipContent}
            placement="auto"
            width="250px"
          >
            <Button
              iconStart={<Icon icon={faPlus} />}
              primary
              onClick={handleClickCreateRule}
            >
              {copyText.createRuleButtonLabel}
            </Button>
          </Tooltip>
        </Flex>
      </Flex>
      <BillingRuleTable
        isLoading={isLoadingRules || isLoadingUsers}
        isMspAdmin={isMspAdminLocation}
        rules={rules}
        onInteraction={handleInteraction}
      />
    </>
  );
}
